import { createRoot, Root } from 'react-dom/client';
import type { ExerciseJSON } from '../transformers/zod-schemas';
import { ExerciseRenderer } from './components/ExerciseRenderer';
import type { UserAnswerValue } from './types';

export interface RenderOptions {
  container: HTMLElement | string;
  json: ExerciseJSON;
  onSubmit?: (values: Record<string, UserAnswerValue>) => void;
  onChange?: (values: Record<string, UserAnswerValue>) => void;
  showSubmitButton?: boolean;
  className?: string;
}

/**
 * DOM helper for quick usage / demos.
 *
 * This couples the engine to DOM + React root creation.
 * For production use, prefer <ExerciseRenderer /> directly in your React tree.
 *
 * @example
 * ```typescript
 * import { compile, renderExercise } from 'math-exercise-engine';
 *
 * const json = compile(dsl);
 * const unmount = renderExercise({
 *   container: '#app',
 *   json,
 *   onSubmit: (values) => {
 *     // Parent decides correctness
 *     const result = validate(json, values);
 *     console.log(result.isCorrect);
 *   }
 * });
 *
 * // Later: unmount()
 * ```
 */
export function renderExercise(options: RenderOptions): () => void {
  const container =
    typeof options.container === 'string'
      ? document.querySelector(options.container)
      : options.container;

  if (!container) {
    throw new Error(`Container not found: ${options.container}`);
  }

  const root: Root = createRoot(container);

  root.render(
    <ExerciseRenderer
      json={options.json}
      onSubmit={options.onSubmit}
      onChange={options.onChange}
      showSubmitButton={options.showSubmitButton}
      className={options.className}
    />
  );

  return () => {
    root.unmount();
  };
}

